export const MAX_DEPTH = 4;                  // 5 niveaux : 0..4
export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 Mo

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} Ko`;
  if (bytes < 1024 ** 3) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
  return `${(bytes / (1024 ** 3)).toFixed(1).replace('.', ',')} Go`;
}

export type PreviewKind = 'pdf' | 'image' | null;

/** PDF réel : MIME officiel, ou `.pdf` rangé en octet-stream (upload navigateur). */
export function looksLikePdf(mime?: string | null, filename?: string | null): boolean {
  if ((mime ?? '').toLowerCase() === 'application/pdf') return true;
  const name = (filename ?? '').toLowerCase();
  if (!name.endsWith('.pdf')) return false;
  const m = (mime ?? '').toLowerCase();
  return m === '' || m === 'application/octet-stream' || m === 'binary/octet-stream';
}

export function isPreviewable(mime: string, filename?: string | null): PreviewKind {
  if (looksLikePdf(mime, filename)) return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  return null;
}

export function buildStoragePath(dossierId: string, fileId: string, filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) return `${dossierId}/${fileId}`;
  const ext = filename.slice(lastDot + 1);
  return `${dossierId}/${fileId}.${ext}`;
}

/** New storage object for a replace (same GED row). First path segment stays the dossier id. */
/** Aperçu / téléchargement : PDF figé (paraphes + signature) s’il existe. */
export function preferSignedStoragePath(
  storagePath: string,
  signedStoragePath: string | null | undefined,
): string {
  if (signedStoragePath && signedStoragePath.length > 0) return signedStoragePath;
  return storagePath;
}

export function buildRevisionStoragePath(dossierId: string, fileId: string, filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  const ext = lastDot > 0 ? filename.slice(lastDot + 1) : 'bin';
  return `${dossierId}/${fileId}.r${crypto.randomUUID()}.${ext}`;
}

export function depthFromParent(parent: { depth: number } | null): number {
  if (parent === null) return 0;
  if (parent.depth >= MAX_DEPTH) {
    throw new Error('max folder depth reached');
  }
  return parent.depth + 1;
}

/** Normalise une chaîne : minuscule + suppression des accents. */
function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Filtre une liste de fichiers par sous-chaîne de nom (insensible casse/accents). */
export function filterFiles<T extends { filename: string }>(files: T[], query: string): T[] {
  const q = normalize(query.trim());
  if (q === '') return files;
  return files.filter((f) => normalize(f.filename).includes(q));
}
